// import external libraries
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// import components
import {
  GridCompact12,
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';
import BacklogEmployeeViewFields from '../BacklogEmployeeViewFields';
import BacklogEntitySearch from '../BacklogEntitySearch';
import BacklogFilterSectionHeading from '../BacklogFilterSectionHeading';
import BacklogOrganizationalUnitFilter from '../BacklogOrganizationalUnitFilter';

// import hooks
import { usePickedOrganizationalUnits } from 'hooks/usePickedOrganizationalUnits';

// import utils
import { missingRequiredFilter } from '../../../../Backlog.utils';
import { organizationalUnitCostCenterIds } from 'utils/organizationalUnits';

// import styles
import {
  BacklogFilterFieldBox,
  BacklogFilterSectionStack
} from '../../../../Backlog.styles';

// import types
import type { BacklogOrganizationalUnitId } from '../../../../Backlog.types';
import type { BacklogOrganizationalUnitOption } from '../BacklogOrganizationalUnitFilter/BacklogOrganizationalUnitFilter.types';
import type {
  BacklogEntityFilterType,
  BacklogFilterFieldsProps,
  BacklogFiltersForm
} from '../../BacklogFilters.types';

/**
 * The body of the filter popover: the plan unit, which applies to every list, then the filters of the
 * entity whose list is on screen, under a heading naming that entity.
 *
 * A filter picked for another entity type is remembered and applies again when you switch to that view,
 * but it does not narrow the list you are looking at now.
 *
 * Either the plan unit or the person is required, never both, so the two fields are marked only while
 * neither is filled.
 */
const BacklogFilterFields = ({
  rowEntityType,
  startDate,
  endDate
}: BacklogFilterFieldsProps) => {
  'use memo';

  const { t: tCommon } = useTranslation('common');
  const { control, setValue } = useFormContext<BacklogFiltersForm>();

  // The dependent fields follow the picks above them as they are made rather than waiting for Toepassen.
  // useWatch rather than watch(): a watch() call in a 'use memo' component can be memoised into a stale
  // value.
  const pickedOrganizationalUnitIds = useWatch({
    control,
    name: 'organizationalUnitIds'
  });
  const pickedEntityFilters = useWatch({
    control,
    name: rowEntityType as BacklogEntityFilterType
  });
  const { organizationalUnits: pickedOrganizationalUnits } =
    usePickedOrganizationalUnits(pickedOrganizationalUnitIds);
  const pickedCostCenterIds = organizationalUnitCostCenterIds(
    pickedOrganizationalUnits
  );

  // The same predicate the page holds its request on, fed the draft rather than the applied filters so
  // it answers the last pick.
  const scopeMissing =
    missingRequiredFilter({
      rowEntityType,
      entityFilters: pickedEntityFilters ?? [],
      organizationalUnitIds: pickedOrganizationalUnitIds
    }) !== null;

  /**
   * A plan unit change. Dropping one clears the people picked from it: those lists do not record which
   * plan unit a pick came from, so the only safe move is to clear them all. Adding a plan unit
   * invalidates nothing, so those picks stay.
   *
   * The functions are deliberately left alone. They are a tenant-wide list that no plan unit ever
   * narrowed, so a pick among them cannot have come from the plan unit being dropped.
   */
  const handleOrganizationalUnitsChange =
    (onChange: (pickedIds: BacklogOrganizationalUnitId[]) => void) =>
    (picked: BacklogOrganizationalUnitOption[]) => {
      const pickedIds = picked.map(({ uniqId }) => uniqId);
      const droppedOne = pickedOrganizationalUnitIds.some(
        (uniqId) => !pickedIds.includes(uniqId)
      );

      if (droppedOne) {
        setValue('CLIENT', []);
        setValue('EMPLOYEE', []);
      }

      onChange(pickedIds);
    };

  const entitySearch = (entityType: BacklogEntityFilterType) => (
    <BacklogFilterFieldBox>
      <Controller
        name={entityType}
        control={control}
        render={({ field }) => (
          <BacklogEntitySearch
            entityType={entityType}
            label={tCommon(`mrp.entityTypeMap.${entityType}.single`)}
            selected={field.value}
            onChange={field.onChange}
            // Marked only while the plan unit is empty too. No entity-type guard needed:
            // missingRequiredFilter answers null for WORKPLACE and RESOURCE.
            required={scopeMissing}
            // The plan unit narrows this field and, once picked, turns it into a list of that unit's
            // people. WORKPLACE and RESOURCE have no plan unit and keep searching the tenant.
            organizationalUnitIds={
              entityType === 'CLIENT' ? pickedCostCenterIds : undefined
            }
          />
        )}
      />
    </BacklogFilterFieldBox>
  );

  const fieldsForView = () => {
    switch (rowEntityType) {
      case 'EMPLOYEE':
        return (
          <BacklogEmployeeViewFields
            organizationalUnitCostCenterIds={pickedCostCenterIds}
            startDate={startDate}
            endDate={endDate}
            scopeMissing={scopeMissing}
          />
        );
      case 'WORKPLACE':
        return <GridCompact6>{entitySearch('WORKPLACE')}</GridCompact6>;
      case 'RESOURCE':
        return <GridCompact6>{entitySearch('RESOURCE')}</GridCompact6>;
      default:
        return <GridCompact6>{entitySearch('CLIENT')}</GridCompact6>;
    }
  };

  return (
    <BacklogFilterSectionStack>
      <GridContainer spacing={3} width={1}>
        <GridCompact12>
          <BacklogFilterFieldBox>
            <Controller
              name="organizationalUnitIds"
              control={control}
              render={({ field }) => (
                <BacklogOrganizationalUnitFilter
                  selected={pickedOrganizationalUnits}
                  onChange={handleOrganizationalUnitsChange(field.onChange)}
                  // A middel belongs to no plan unit, so there is nothing for this field to narrow on
                  // the middelen view.
                  disabled={rowEntityType === 'RESOURCE'}
                  // Marked only while the person field is empty as well: the asterisk means "pick one
                  // of these", not "pick this one".
                  required={scopeMissing}
                />
              )}
            />
          </BacklogFilterFieldBox>
        </GridCompact12>
      </GridContainer>
      <BacklogFilterSectionHeading entityType={rowEntityType} />
      <GridContainer spacing={3} width={1}>
        {fieldsForView()}
      </GridContainer>
    </BacklogFilterSectionStack>
  );
};

export default BacklogFilterFields;
